import { z } from 'zod';

import { formFieldSchema, promoFieldSchema, labelsSchema, formCascadeRuleSchema, fieldRuleSchema } from './feFieldSchemas';

/**
 * FE contract for the backend `GET /tpo_gen/config/` response.
 *
 * This schema is the single source of truth for both the runtime check
 * (`customerConfigSchema.safeParse`) and the TypeScript types (`z.infer`).
 * When the backend contract changes, update this file only.
 *
 * Rules:
 * - Load-bearing fields are REQUIRED (no `.optional()`/`.catch()`) so a missing
 *   one fails loudly at the config gate instead of silently breaking the UI.
 * - Genuinely tolerant fields use `.optional().default(...)`.
 * - Unknown extra fields are stripped by default (forward-compatible).
 */

// Metric DEFINITION (`metrics[]`). Drives the editable promo/spend grid (split
// by `group`) and supplies definitions joined into the report `metric_views`.
export const applicableWhenConditionSchema = z.object({
    attribute: z.string(),
    operator: z.string(),
    values: z.array(z.string()),
});

export const metricSchema = z.object({
    // Load-bearing — required.
    key: z.string().min(1),
    display_name: z.string().min(1),
    group: z.string().min(1),
    order: z.number(),
    default_visible: z.boolean(),
    editable: z.boolean(),
    precision: z.number(),
    applicable_promotion_types: z.array(z.string()).optional(),
    applicable_when: z.array(applicableWhenConditionSchema).optional(),
    // Tolerant — nullable + defaulted (backend may send these as null).
    min_value: z.number().nullable().optional().default(null),
    max_value: z.number().nullable().optional().default(null),
    currency_prefix: z.string().nullable().optional().default(''),
    currency_suffix: z.string().nullable().optional().default(''),
    // Optional metadata (backend may send these as null).
    data_type: z.string().nullable().optional(),
    formula: z.string().nullable().optional(),
    fe_formula: z.string().nullable().optional(),
    depends_on: z.array(z.string()).nullable().optional(),
    source: z.string().optional(),
    render_mode: z.string().optional(),
});

// `metric_views` is OPTIONAL and NOT consumed at runtime (the MetricPage KPI
// catalog is built from `metrics[]` group `kpi_metrics`). It is validated only
// as a loose, tolerant array so that varied/partial backend shapes (e.g.
// metadata columns sending `metric_key: null`) never block the module.
export const metricViewSchema = z.record(z.unknown());

/**
 * Backend-provided static FE config (the former static `tpoGenConfig` object).
 *
 * The form-field arrays (`eventDetailsFields`, `promoDetailsFields`,
 * `promoFields`, `planFields`) and `labels` are validated at render-critical
 * strictness via the schemas in `feFieldSchemas.ts` so a malformed entry fails
 * loud at the gate instead of crashing a component. The remaining keys stay
 * coarse (right container type only); their deep shapes are cast to the FE TS
 * types inside `feConfigAdapter`. `.passthrough()` keeps any extra UI keys the
 * backend adds ahead of the FE, and the strict element schemas above also
 * passthrough save-only fields.
 */
export const feConfigSchema = z
    .object({
        showEditableIndicators: z.boolean().optional().default(false),
        eventDetailsFields: z.array(formFieldSchema),
        promoDetailsFields: z.array(formFieldSchema),
        promoFields: z.array(promoFieldSchema).min(1),
        upliftConfig: z.record(z.unknown()),
        formCascadeRules: z.array(formCascadeRuleSchema).optional().default([]),
        fieldRules: z.array(fieldRuleSchema).optional().default([]),
        // Metric grid applicability rules (same FieldRule shape; target-only
        // `metric:<key>` scope). Validated like fieldRules; consumed via
        // useTpoGenConfig().metricRules.
        metricRules: z.array(fieldRuleSchema).optional().default([]),
        planFields: z.array(formFieldSchema).min(1),
        planIndexColumns: z.array(z.record(z.unknown())).min(1),
        calendarIntervalOptions: z.array(z.record(z.unknown())),
        allDefaultColumns: z.array(z.record(z.unknown())),
        defaultRowActionItems: z.array(z.record(z.unknown())),
        planActionMenuItems: z.array(z.record(z.unknown())),
        labels: labelsSchema,
        planIndexFilterKeys: z.array(z.record(z.unknown())),
        filterDimensionInfo: z.record(z.unknown()),
        calendarFilterKeys: z.array(z.record(z.unknown())),
        calendarFilterDimensionInfo: z.record(z.unknown()),
        calendarGroupByOptions: z.array(z.record(z.unknown())),
        // MetricPage toolbar/view config. Optional: adapted (and defaulted) in
        // `feConfigAdapter` against the `MetricPageConfig` type.
        metricPageConfig: z.record(z.unknown()).optional(),
        optimizePlanConfig: z.record(z.unknown()).optional(),
        optimizeResultConfig: z.record(z.unknown()).optional(),
        importEventFromPlanConfig: z.record(z.unknown()),
    })
    .passthrough();

export const metricGroupSchema = z.object({
    key: z.string().min(1),
    display_name: z.string().min(1),
    order: z.number().optional().default(0),
});

export const promotionTypeSchema = z.object({
    key: z.string().min(1),
    display_name: z.string().min(1),
    fund_type: z.string().optional().default(''),
    properties: z.record(z.unknown()).optional().default({}),
});

export const tacticSchema = z.object({
    key: z.string().min(1),
    code: z.string().optional().default(''),
    display_name: z.string().min(1),
});

// Generic business-attribute declaration (`entity_attributes[]`). Drives the
// dynamic `attributes` codec: which keys live in the nested `attributes` object
// per entity level, and where their dropdown options come from. New attributes
// can be added here (backend) + a matching field config (`apiKey: 'attributes.<key>'`)
// without touching any payload/response hook.
export const entityAttributeSchema = z.object({
    key: z.string().min(1),
    display_name: z.string().min(1),
    level: z.string().min(1), // 'plan' | 'event' | 'promotion'
    data_type: z.string().min(1),
    required: z.boolean().optional().default(false),
    options_source: z.string().nullable().optional().default(null),
    options: z
        .array(z.object({ key: z.string(), display_name: z.string() }))
        .nullable()
        .optional()
        .default(null),
    default_value: z.unknown().nullable().optional().default(null),
    depends_on: z.array(z.string()).nullable().optional().default(null),
    applicable_when: z.array(z.record(z.unknown())).nullable().optional().default(null),
});

// Report-level KPI catalog entry (Plan Metric view).
export const kpiSchema = z.object({
    key: z.string().min(1),
    display_name: z.string().min(1),
    sortable: z.boolean().optional().default(true),
});

export const customerConfigSchema = z.object({
    // Required — the FE cannot render the module without these.
    metrics: z.array(metricSchema).min(1),
    metric_groups: z.array(metricGroupSchema).min(1),
    // Optional + loosely typed: the FE no longer consumes `metric_views` at
    // runtime (the KPI catalog is derived from `metrics` group `kpi_metrics`),
    // so neither its absence nor its internal shape blocks the module.
    metric_views: z.array(metricViewSchema).optional(),
    fe_config: feConfigSchema,
    promotion_types: z.array(promotionTypeSchema).min(1),
    tactics: z.array(tacticSchema).min(1),
    // Generic attribute schema driving the dynamic `attributes` codec. Tolerant:
    // defaults to [] so a not-yet-migrated backend does not block the module
    // (the codec falls back to field-config-derived keys when empty).
    entity_attributes: z.array(entityAttributeSchema).optional().default([]),
    dim1_label: z.string().min(1),
    dim2_label: z.string().min(1),
    currency_code: z.string().min(1),
    // Tolerant — defaulted so absence does not block the module.
    kpis: z.array(kpiSchema).optional().default([]),
    year_label: z.string().optional(),
    offer_methods: z.array(z.unknown()).optional().default([]),
    validation_rules: z.array(z.unknown()).optional().default([]),
    visibility_profiles: z.record(z.unknown()).optional().default({}),
});

export type ZodIssueLike = {
    path: (string | number)[];
    message: string;
};

/**
 * Thrown when the config response (live or fallback) fails schema validation
 * or referential-integrity checks in an adapter. Carrying the issue list lets
 * the gate render the offending field paths for debugging.
 */
export class ConfigValidationError extends Error {
    readonly issues: ZodIssueLike[];

    constructor(issues: ZodIssueLike[]) {
        const summary = issues
            .slice(0, 5)
            .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
            .join('; ');
        super(`TPO Gen config validation failed: ${summary}`);
        this.name = 'ConfigValidationError';
        this.issues = issues;
    }
}
